import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

const ACT = [
  { value: 1.2, label: 'Sedentary' },
  { value: 1.375, label: 'Light: exercise 1-3 times/week' },
  { value: 1.465, label: 'Moderate: exercise 4-5 times/week' },
  { value: 1.55, label: 'Active: 3-4 intense sessions/week' },
  { value: 1.725, label: 'Very Active: 6-7 intense sessions/week' },
  { value: 1.9, label: 'Extra Active: physical job' },
];

// Protein requirements g per kg body weight based on activity + goal
const RATIOS = {
  sedentary: [0.8, 1.0],
  light: [1.0, 1.2],
  moderate: [1.2, 1.4],
  athlete: [1.4, 1.8],
  bulk: [1.6, 2.2],
};

export default function ProteinCalc() {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('male');
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [lb, setLb] = useState(160);
  const [cm, setCm] = useState(178);
  const [kg, setKg] = useState(73);
  const [activity, setActivity] = useState('moderate');
  const [pregnancy, setPregnancy] = useState('none');
  const clear = () => { setAge(25); setGender('male'); setFt(5); setInches(10); setLb(160); setCm(178); setKg(73); setActivity('moderate'); setPregnancy('none'); };

  const c = useMemo(() => {
    const weightKg = tab === 0 ? (+lb || 0) * 0.453592 : +kg || 0;
    const [lo, hi] = RATIOS[activity];
    let extra = 0;
    if (pregnancy === 'pregnant') extra = 25;
    else if (pregnancy === 'breastfeed') extra = 20;
    return { weightKg, min: weightKg * lo + extra, max: weightKg * hi + extra, lo, hi };
  }, [tab, lb, kg, activity, pregnancy]);

  return (
    <CalcShell look="cnet" title="Protein Calculator" subtitle="Estimate daily protein requirement for maintenance, sports, and clinical needs.">
      <CalcNetPanel
        slug="pr"
        activeTab={tab}
        onTabChange={setTab}
        onClear={clear}
        settings={
          gender === 'female' && (
            <Row label="Status">
              <select value={pregnancy} onChange={e => setPregnancy(e.target.value)} data-testid="pr-preg"
                className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="none">Not pregnant / breastfeeding</option>
                <option value="pregnant">Pregnant</option>
                <option value="breastfeed">Breastfeeding</option>
              </select>
            </Row>
          )
        }
        right={
          <div>
            <div className="text-lg text-slate-800">Suggested daily protein intake</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="pr-range">{c.min.toFixed(0)} – {c.max.toFixed(0)} g/day</div>
            <p className="text-sm text-slate-700 mt-1">≈ {c.lo} – {c.hi} g per kg body weight</p>
            <ResultTable
              testid="pr-source-table"
              headers={['Food (approx)', 'Protein (g)']}
              rows={[
                ['1 large egg', '6'],
                ['100 g chicken breast', '31'],
                ['100 g cooked lentils', '9'],
                ['1 cup Greek yogurt (240 g)', '17'],
                ['1 scoop whey protein', '24'],
                ['100 g tofu', '8'],
              ]}
            />
          </div>
        }
      >
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="pr-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages 18 – 80</span></Row>
        <Row label="Gender"><RadioGroup name="pr-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        {tab === 0 ? <>
          <Row label="Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="pr-h" /></div></Row>
          <Row label="Weight"><UnitInput value={lb} onChange={setLb} unit="pounds" testid="pr-wlb" /></Row>
        </> : <>
          <Row label="Height"><UnitInput value={cm} onChange={setCm} unit="cm" testid="pr-hcm" /></Row>
          <Row label="Weight"><UnitInput value={kg} onChange={setKg} unit="kg" testid="pr-wkg" /></Row>
        </>}
        <Row label="Activity">
          <select value={activity} onChange={e => setActivity(e.target.value)} data-testid="pr-activity"
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
            <option value="sedentary">Sedentary (0.8–1.0 g/kg)</option>
            <option value="light">Light activity (1.0–1.2 g/kg)</option>
            <option value="moderate">Moderate exercise (1.2–1.4 g/kg)</option>
            <option value="athlete">Athlete / high performance (1.4–1.8 g/kg)</option>
            <option value="bulk">Building muscle (1.6–2.2 g/kg)</option>
          </select>
        </Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Body weight ≈ ${c.weightKg.toFixed(1)} kg`,
        `Range = weight × (${c.lo} – ${c.hi}) g/kg = <b>${c.min.toFixed(0)} – ${c.max.toFixed(0)} g/day</b>`,
      ]} />
    </CalcShell>
  );
}
